import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Shield, BarChart2, Globe, Layers, ArrowRight, CheckCircle,
  XCircle, AlertCircle, Star, TrendingUp, Users, Cpu, Lock, Activity,
  ChevronRight, Play, Award, DollarSign, RefreshCw, GitBranch, Sparkles,
  Megaphone, Wallet, ShieldCheck, Target, Radio, Receipt, Building2, Eye, Zap, Compass,
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import Seo from '../components/Seo';
import PublicFooter from '../components/PublicFooter';
import { PLATFORM_FEATURE_COUNT } from '../data/platformStats';

// ── Logo / Brand ──────────────────────────────────────────────────────────────
function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <img src={logo} alt="haythix" className="w-9 h-9" />
      <span className="font-bold text-white text-lg leading-none">haythix</span>
    </div>
  );
}

// ── Comparison data ───────────────────────────────────────────────────────────
const COMPARISON = [
  { feature: 'DEX + CEX trading unified',   haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'partial', pionex: 'none',    coinrule: 'none' },
  { feature: 'Pay subscription in USDC (no card)', haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
  { feature: 'AI signals & forecasting',    haythix: 'full', commas: 'partial', hopper: 'partial', bitsgap: 'partial', pionex: 'none',    coinrule: 'partial' },
  { feature: 'AI strategy generator',       haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'none',    pionex: 'none',    coinrule: 'none' },
  { feature: 'Multi-chain DEX trading',     haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'none',    pionex: 'none',    coinrule: 'none' },
  { feature: 'Live community & chat',       haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'none',    pionex: 'none',    coinrule: 'none' },
  { feature: 'Verified call tracking',      haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'none',    pionex: 'none',    coinrule: 'none' },
  { feature: 'Leaderboards & copy trading', haythix: 'full', commas: 'partial', hopper: 'partial', bitsgap: 'none',    pionex: 'full',    coinrule: 'none' },
  { feature: 'USDC tip jar',               haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'none',    pionex: 'none',    coinrule: 'none' },
  { feature: 'Elite AI Suite (6 tools)',    haythix: 'full', commas: 'none',    hopper: 'none',    bitsgap: 'none',    pionex: 'none',    coinrule: 'none' },
  { feature: '5-layer risk controls',       haythix: 'full', commas: 'partial', hopper: 'partial', bitsgap: 'partial', pionex: 'partial', coinrule: 'partial' },
  { feature: 'Portfolio rebalancing',       haythix: 'full', commas: 'partial', hopper: 'partial', bitsgap: 'partial', pionex: 'none',    coinrule: 'none' },
  // ── Seven signature Verticals — every checkmark backed by a public API endpoint
  { feature: 'Non-custodial Wallet-as-Bot (signed policy + hard caps)', haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
  { feature: 'AI forecast accuracy with Wilson 95% CI on real OHLCV',   haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
  { feature: 'Verified-creator leaderboard (Wilson lower bound, not raw win-rate)', haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
  { feature: 'Tax-loss harvest + what-if on real fills (no imputed prices)',       haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
  { feature: 'B2B Tokenomics Studio + public investor report URL',                 haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
  { feature: 'Token Universe Scanner with universe-stats discipline + methodology hash', haythix: 'full', commas: 'none', hopper: 'none', bitsgap: 'none', pionex: 'none', coinrule: 'none' },
];

// ── Seven signature Verticals (depth moats — every claim verifiable) ───────────
const VERTICALS = [
  {
    n: 1, icon: ShieldCheck, color: 'from-emerald-500 to-emerald-700',
    title: 'Wallet-as-Bot',
    tagline: 'Non-custodial automation with a policy you sign.',
    body: 'EIP-712 signed session policy declares hard caps ($100k per trade, $1M per session, 30-day max) and a forbidden-operations floor that even a malicious frontend cannot raise.',
    endpoint: 'GET /api/wallet-sessions/policy',
  },
  {
    n: 2, icon: Target, color: 'from-blue-500 to-blue-700',
    title: 'Forecast Accuracy Track Record',
    tagline: 'Every AI signal scored against real OHLCV.',
    body: 'Wilson 95% confidence interval on real outcomes only, neutral moves under 0.1% excluded so we never claim credit for noise. Sample size always visible — small-n forecasts automatically de-ranked.',
    endpoint: 'GET /api/forecast-accuracy/summary',
  },
  {
    n: 3, icon: Radio, color: 'from-violet-500 to-violet-700',
    title: 'Verified Creator + Signal Channels',
    tagline: 'Leaderboards ranked by statistics, not luck.',
    body: 'Verified badge requires 20+ decided calls AND 60%+ accuracy AND 30+ days active. Sorted by Wilson lower bound, so a careful 50-call creator outranks a lucky 3-call one.',
    endpoint: 'GET /api/verified-creators/policy',
  },
  {
    n: 4, icon: Receipt, color: 'from-amber-500 to-amber-700',
    title: 'Tax-Loss Harvest + What-If Scenarios',
    tagline: 'Real fills, real prices, real risk metrics.',
    body: 'FIFO lots from your actual bot fills. Stable-quote-only USD anchoring. Conservative replacement map (ETH→WSTETH, BTC→WBTC, SOL→MSOL). What-if Sharpe / VaR / drawdown over real Binance OHLCV. No imputed prices, ever.',
    endpoint: 'GET /api/tax-strategy/replacement-map',
  },
  {
    n: 5, icon: Building2, color: 'from-cyan-500 to-cyan-700',
    title: 'Tokenomics-as-a-Service B2B',
    tagline: 'Workspace + investor report URL + API key.',
    body: 'Multi-token issuer workspace. Live holder analysis from real chain explorers (Gini, top-10/top-50 concentration). AMM unlock-impact simulator. Public investor report URL. SHA256-hashed API keys for embedding.',
    endpoint: 'GET /api/tokenomics/r/{publicSlug}',
  },
  {
    n: 6, icon: Compass, color: 'from-teal-500 to-teal-700',
    title: 'Token Universe Scanner',
    tagline: 'Cross-chain scanner with universe-stats discipline.',
    body: 'Define criteria (volume spike, price breakout, liquidity floor, age range), apply across whole chains. Every run discloses universe stats (considered/included/excluded by reason), partial-run reasons, data freshness, and a SHA-256 methodology hash for reproducibility.',
    endpoint: 'GET /api/scanner/policy',
  },
];

// ── Six Phases (foundation that puts us at parity-or-better) ────────────────
const PHASES = [
  { n: 1, icon: Layers,    title: 'Portfolio Rebalancer',           desc: 'Real positions, hourly worker, transparent "unpriced" status when CoinGecko has no feed.' },
  { n: 2, icon: BarChart2, title: 'Grid Bot Variants',              desc: 'Spot, futures, infinity, reverse — one engine, four modes, correct side discipline on each rung.' },
  { n: 3, icon: RefreshCw, title: 'Cross-Exchange Arbitrage',       desc: 'Parallel legs with a 5-state status (ok/partial/partial-leg/open-leg/unfilled). No ghost P&L.' },
  { n: 4, icon: Eye,       title: 'QA Sweep — no fabricated metrics', desc: 'Stripped 15+ hardcoded ROI/winRate values, replaced with transparent "pending" state. The phase competitors never run.' },
  { n: 5, icon: Zap,       title: 'Frontend Deploy Discipline',     desc: 'Every shipped page is callable end-to-end. No half-shipped routes pointing at undeployed backends.' },
  { n: 6, icon: Lock,      title: 'Ops Cleanup with Safety Nets',   desc: 'Destructive ops require explicit token (e.g. "YES_DELETE_<userId>") and snapshot prior state. Mistakes are reversible.' },
];

function CompIcon({ val }) {
  if (val === 'full')    return <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto" />;
  if (val === 'partial') return <AlertCircle className="w-5 h-5 text-amber-400 mx-auto" />;
  return <XCircle className="w-5 h-5 text-gray-600 mx-auto" />;
}

// ── Feature cards ─────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: Bot,         color: 'from-amber-400 to-amber-600',
    title: 'DEX + CEX Trading Bots',
    desc:  'The only platform that runs Smart Trade, Grid, DCA, and Algo Order bots on BOTH decentralized exchanges (Uniswap, PancakeSwap) AND centralized exchanges (Binance, Coinbase, Kraken + 12 more). One platform, every venue.'
  },
  {
    icon: Cpu,         color: 'from-purple-500 to-purple-700',
    title: 'AI Trading Intelligence',
    desc:  'AI-assisted buy/sell signals, market sentiment scoring, and automated strategy generation. Describe your trading idea in plain English — the system builds the strategy.'
  },
  {
    icon: Globe,       color: 'from-blue-500 to-blue-700',
    title: 'Multi-Chain DEX Trading',
    desc:  'Native DEX trading across 15 chains — Ethereum, BSC, Polygon, Arbitrum, Base, Avalanche, Optimism, Solana, Cronos, Fantom, zkSync Era, Mantle, Linea, Scroll, Blast. Full bot suite (Limit Orders, Stop Orders, Smart Trade, Grid Bot, DCA, Algo Orders, Conditional Bot, Cross-chain Arbitrage) with MEV-protected execution via CoW, Uniswap V3, Aerodrome, SushiSwap, Curve, and more.'
  },
  {
    icon: Building2,   color: 'from-blue-500 to-cyan-600',
    title: 'Multi-Exchange CEX Trading',
    desc:  '15 centralized exchanges integrated via CCXT — including Binance, Coinbase, Kraken, Bybit, OKX, KuCoin, and Gate.io. Spot trading on all 15. Perpetual futures on 8 (Binance, Bybit, OKX, KuCoin, Bitget, Gate.io, HTX, MEXC). Unified balance view across the lot.',
    badge: 'NEW'
  },
  {
    icon: Target,      color: 'from-cyan-500 to-blue-600',
    title: 'Institutional Algo Orders',
    desc:  'TWAP, VWAP, Iceberg, and Bracket / OCO execution algorithms. Slice large orders to minimize market impact, dollar-cost-average over hours or days, hide size with iceberg, and pair entries with one-cancels-other risk controls.'
  },
  {
    icon: Users,       color: 'from-violet-500 to-violet-700',
    title: 'Live Trading Community',
    desc:  'Built-in chat rooms, verified call tracking with accuracy stats, leaderboards, polls, and USDC tip jar. Follow other traders and see their measured performance — Wilson 95% CI, sample size visible.',
  },
  {
    icon: Sparkles,    color: 'from-orange-500 to-red-600',
    title: 'Elite AI Suite',
    desc:  'AI Risk Analyst chat, Tokenomics Advisor with PDF analysis, Launch Volume Forecaster, Launch Roadmap Planner, Volume Health Widget, and Competitor Tracker — six tools no other platform offers.',
    badge: 'ELITE'
  },
  {
    icon: BarChart2,   color: 'from-teal-500 to-teal-700',
    title: 'Institutional Portfolio Analytics',
    desc:  'Eight-tab dashboard with Sharpe / Sortino / VaR / max drawdown, stress-test scenarios, factor exposures, correlation heatmap, Kelly criterion sizing, pairs analysis, and whale-flow tracking.'
  },
  {
    icon: Shield,      color: 'from-emerald-500 to-emerald-700',
    title: '5-Layer Risk Controls',
    desc:  'Per-wallet spending limits, portfolio drawdown protection, volatility-based auto-pause, emergency kill switch, and trailing stops. Set the rules once; bots trade within them.'
  },
  {
    icon: ShieldCheck, color: 'from-emerald-500 to-teal-600',
    title: '7 Verified Verticals',
    desc:  'Every signature feature publishes a public methodology endpoint you can read before you sign up — wallet policy, forecast accuracy, verified-creator rules, tax replacement map, tokenomics report, scanner methodology hash.',
  },
  {
    icon: RefreshCw,   color: 'from-cyan-500 to-cyan-700',
    title: 'Cross-Exchange Arbitrage',
    desc:  'Detect real-time price differences across DEX and CEX venues. Net-profit calculator includes gas fees, bridge costs, and slippage. 5-state leg status — no ghost P&L from half-filled orders.'
  },
  {
    icon: Layers,      color: 'from-rose-500 to-rose-700',
    title: 'Portfolio Automation',
    desc:  'Automated rebalancing, DCA execution, and cross-chain position tracking. Full portfolio heatmap with exposure and risk breakdowns per chain. Transparent "unpriced" status when CoinGecko has no feed.'
  },
];

// ── Pricing Section Component ─────────────────────────────────────────────────

const PLAN_SPECS = {
  starter: { bots: '2', wallets: '5 per bot',  chains: '3 (ETH + Base + Polygon)',         history: '14 days', strategies: 'Basic',           alerts: 'Email',                       support: 'Help center' },
  trader:  { bots: '5', wallets: '10 per bot', chains: '8 EVM chains',                    history: '30 days', strategies: 'Advanced',        alerts: 'Email + Telegram',            support: 'Priority' },
  pro:     { bots: '8', wallets: '10 per bot', chains: '12 chains (EVM + Solana)',        history: '90 days', strategies: 'All',             alerts: 'Email + Telegram + Webhook',  support: 'Dedicated' },
  elite:   { bots: '10',wallets: '10 per bot', chains: 'All 15 chains',                    history: '1 year', strategies: 'All + Exclusive', alerts: 'All + SMS + Discord',         support: 'Dedicated priority' },
};

const ELITE_EXCLUSIVE = [
  'Launch Volume Forecaster',
  'AI Risk Analyst (haythix AI)',
  'Launch Roadmap Planner',
  'Volume Health Widget (embeddable)',
  'Competitor Activity Tracker',
  'AI Tokenomics Advisor (PDF analysis)',
];

const PLAN_INHERITS = { starter: null, trader: 'Starter', pro: 'Trader', elite: 'Pro' };

function PlanDetailModal({ plan, isAnnual, onClose, navigate }) {
  if (!plan) return null;
  const displayPrice = isAnnual ? plan.price : plan.monthlyPrice;
  const savingsPercent = Math.round((1 - plan.price / plan.monthlyPrice) * 100);
  const annualTotal = Math.round(plan.price * 12);
  const specs = PLAN_SPECS[plan.id] || {};
  const sections = Object.entries(plan.detailedFeatures || {});
  const inheritsFrom = PLAN_INHERITS[plan.id];
  const isElite = plan.id === 'elite';
  const missingElite = !isElite;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-[#0d1017] border border-dark-600 shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-20 text-gray-500 hover:text-white transition-colors">
          <XCircle className="w-5 h-5" />
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className={`px-6 pt-6 pb-5 ${isElite ? 'bg-gradient-to-br from-amber-500/8 via-orange-500/5 to-transparent' : ''}`}>
            <div className="flex items-start justify-between pr-8">
              <div>
                <div className="flex items-center gap-2.5 mb-1.5">
                  <h2 className={`text-2xl font-bold ${plan.accentColor}`}>{plan.label}</h2>
                  {plan.badge && (
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide uppercase ${
                      isElite ? 'bg-amber-400/15 text-amber-400 border border-amber-400/25'
                        : plan.highlight ? 'bg-cyan-400/15 text-cyan-400 border border-cyan-400/25'
                        : 'bg-violet-400/15 text-violet-400 border border-violet-400/25'
                    }`}>{plan.badge}</span>
                  )}
                </div>
                <p className="text-sm text-gray-400">{plan.tagline}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-white">${displayPrice}</span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
                {isAnnual ? (
                  <div className="mt-1">
                    <span className="text-xs text-gray-500 line-through">${plan.monthlyPrice}/mo</span>
                    <span className="ml-1.5 text-xs text-emerald-400 font-bold">Save {savingsPercent}%</span>
                    <p className="text-[11px] text-gray-600 mt-0.5">Billed ${annualTotal}/year</p>
                  </div>
                ) : (
                  <p className="text-xs text-emerald-400 mt-1">
                    ${plan.price}/mo if billed annually
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Specs grid — key-value pairs */}
          <div className="px-6 py-4 border-y border-dark-700/60">
            <div className="grid grid-cols-2 gap-x-8 gap-y-0">
              {[
                ['Strategies', specs.strategies],
                ['Alerts', specs.alerts],
                ['Chains', specs.chains],
                ['Support', specs.support],
                ['Trade History', specs.history],
                ['', ''],
              ].filter(([k]) => k).map(([label, value]) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-dark-700/30 last:border-0">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
                  <span className="text-sm text-white font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Inherited features */}
          {inheritsFrom && (
            <div className="px-6 pt-5 pb-1">
              <h3 className="text-sm font-semibold text-emerald-400 mb-3">
                Everything in {inheritsFrom}, plus:
              </h3>
            </div>
          )}

          {/* Feature sections */}
          <div className="px-6 py-4 space-y-5">
            {sections.map(([section, items]) => {
              const isExclusive = section === 'Exclusive Tools';
              return (
                <div key={section}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className={`text-xs font-bold tracking-widest uppercase ${
                      isExclusive ? 'text-amber-400' : 'text-gray-500'
                    }`}>{section}</h3>
                    <div className="flex-1 border-t border-dark-700/40" />
                    {isExclusive && (
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/25 tracking-wider">
                        ELITE ONLY
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {items.map(item => (
                      <div key={item} className="flex items-start gap-2.5 py-1.5">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          isExclusive ? 'text-amber-400' : 'text-emerald-400'
                        }`} />
                        <span className="text-sm text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Security badge bar */}
          <div className="mx-6 mb-4 px-4 py-3 rounded-xl bg-dark-700/40 border border-dark-600/50 flex items-center justify-center gap-3 text-xs text-gray-400">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span>Bank-grade encryption</span>
            <span className="text-dark-500">·</span>
            <span>Non-custodial — your keys, your funds</span>
            <span className="text-dark-500">·</span>
            <span>MFA / TOTP security</span>
          </div>

          {/* What you don't get (FOMO for non-Elite) */}
          {missingElite && (
            <div className="mx-6 mb-5 px-5 py-4 rounded-xl bg-red-500/5 border border-red-500/15">
              <h3 className="text-xs font-bold text-red-400 tracking-widest uppercase mb-3">
                On {plan.label} you don't get:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {ELITE_EXCLUSIVE.map(f => (
                  <div key={f} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-3.5 h-3.5 text-red-400/60 flex-shrink-0" />
                      <span className="text-sm text-gray-500">{f}</span>
                    </div>
                    <span className="text-[10px] text-amber-400/70 font-medium whitespace-nowrap hidden sm:block">from Elite ($149.99/mo)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="px-6 py-4 border-t border-dark-700 bg-[#0d1017] flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => { onClose(); navigate(`/register?plan=${plan.id}`); }}
            className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all ${
              isElite
                ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 hover:from-amber-300 hover:to-orange-300'
                : 'bg-white text-gray-900 hover:bg-gray-100'
            }`}
          >
            {isElite ? 'Go Elite' : `Get Started with ${plan.label}`}
          </button>
          {missingElite && (
            <button
              onClick={() => { onClose(); navigate('/register?plan=elite'); }}
              className="px-4 py-3 rounded-xl text-sm font-medium text-amber-400 border border-amber-400/30 hover:bg-amber-400/10 transition-colors whitespace-nowrap"
            >
              Go Elite
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PricingSection({ navigate }) {
  const [billing, setBilling] = useState('annual');
  const [detailPlan, setDetailPlan] = useState(null);
  const isAnnual = billing === 'annual';

  return (
    <section id="pricing" className="bg-[#0d0e14] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Simple, <span className="text-amber-400">transparent pricing</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-2">
            Lock in these rates before they increase. First 100 paying users become haythix Founders — 50% off forever.
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold mt-2">
            <Activity className="w-3.5 h-3.5" />
            Prices increase in 6 months — lock in now
          </div>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
          <button
            onClick={() => setBilling(b => b === 'annual' ? 'monthly' : 'annual')}
            className={`relative w-14 h-7 rounded-full transition-colors ${isAnnual ? 'bg-emerald-500' : 'bg-dark-600'}`}
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${isAnnual ? 'left-[30px]' : 'left-0.5'}`} />
          </button>
          <span className={`text-sm font-medium ${isAnnual ? 'text-white' : 'text-gray-500'}`}>Annual</span>
          {isAnnual && (
            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30">
              Save up to 50%
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {PLANS.map(plan => {
            const displayPrice = isAnnual ? plan.price : plan.monthlyPrice;
            const savingsPercent = Math.round((1 - plan.price / plan.monthlyPrice) * 100);
            const annualTotal = Math.round(plan.price * 12);

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border p-6 flex flex-col ${plan.color} ${
                  plan.highlight
                    ? 'bg-gradient-to-b from-cyan-500/5 to-transparent ring-1 ring-cyan-400/20'
                    : plan.id === 'elite'
                    ? 'bg-gradient-to-b from-amber-500/5 to-transparent ring-1 ring-amber-400/20'
                    : 'bg-[#0a0b0f]'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                      plan.id === 'elite' ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900'
                        : plan.highlight ? 'bg-cyan-400 text-gray-900'
                        : 'bg-violet-400 text-gray-900'
                    }`}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="mb-5">
                  <h3 className="font-bold text-white text-lg mb-3">{plan.label}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">${displayPrice}</span>
                    <span className="text-gray-500 text-sm">/mo</span>
                  </div>

                  {isAnnual ? (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">
                        <span className="line-through">${plan.monthlyPrice}/mo</span>
                        <span className="ml-2 text-emerald-400 font-semibold">Save {savingsPercent}%</span>
                      </p>
                      <p className="text-xs text-gray-500">Billed ${annualTotal}/year</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">
                      Or <span className="text-emerald-400 font-semibold">${plan.price}/mo</span> billed annually
                    </p>
                  )}
                </div>

                <ul className="space-y-2 flex-1 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        plan.id === 'elite' && f.startsWith('Activity') || f.startsWith('AI Risk') || f.startsWith('Campaign') || f.startsWith('Volume') || f.startsWith('Competitor') || f.startsWith('AI Tokenomics')
                          ? 'text-amber-400'
                          : 'text-emerald-400'
                      }`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/register?plan=${plan.id}`)}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                      plan.id === 'elite'
                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 hover:from-amber-300 hover:to-orange-300'
                        : plan.highlight
                        ? 'bg-white text-gray-900 hover:bg-gray-100'
                        : 'border border-white/10 text-white hover:bg-white/5'
                    }`}
                  >
                    {plan.id === 'elite' ? 'Go Elite' : 'Get Started'}
                  </button>
                  <button
                    onClick={() => setDetailPlan(plan)}
                    className="w-full py-2 text-xs font-medium text-gray-500 hover:text-white transition-colors flex items-center justify-center gap-1"
                  >
                    View all features <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-gray-500 mt-8">
          7-day free trial on all plans. Cancel anytime — no questions asked.
        </p>
      </div>

      {detailPlan && (
        <PlanDetailModal plan={detailPlan} isAnnual={isAnnual} onClose={() => setDetailPlan(null)} navigate={navigate} />
      )}
    </section>
  );
}

// ── Pricing data ─────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'starter', label: 'Starter', price: 10, monthlyPrice: 19.99,
    color: 'border-blue-500/40', accentColor: 'text-blue-400', highlight: false, badge: null,
    tagline: 'Perfect for getting started with automated trading',
    features: [
      'Grid Bot + DCA + Smart Trade Terminal',
      '15 CEX exchanges + 15 DEX chains (unified)',
      'AI Signals (3/day) + Real Backtests',
      'Pay in USDC (no card, multi-chain)',
      'Premium chat: voice notes, trade snapshots',
      'Verified Caller badge + community access',
      'Position Integrity + drift detection',
      'Referral rewards',
    ],
    detailedFeatures: {
      'Trading': ['Grid Bot, DCA, Smart Trade Terminal', '15 CEX exchanges (Binance, Coinbase, Kraken + 12 more)', '15 DEX chains (ETH, BSC, Polygon, Arbitrum, Base, Avalanche, Optimism, Solana, Cronos, Fantom, zkSync Era, Mantle, Linea, Scroll, Blast)', 'Full DEX bot suite: Limit Orders, Stop Orders, Smart Trade, Grid, DCA, Algo Orders, Conditional Bot, Arbitrage', 'TradingView Webhooks (CEX + DEX)', 'Paper trading / simulation'],
      'AI & Signals': ['AI Signals (Buy/Sell/Hold, 3/day)', 'NL Bot Builder (5/month)', 'Real historical backtesting (3/day)', 'Market trend detection'],
      'Crypto Payments': ['Pay subscription in USDC (no card needed)', 'Multi-chain support (Base / Polygon / Arbitrum)', 'On-chain auto-renew via USDC allowance', 'Tip jar (send + receive USDC)'],
      'Community': ['Live chat rooms (per-chain + per-exchange)', 'Voice notes (30s) + trade snapshots', 'Verified Caller badge + Top Calls of the Week', '🔥 Hot Channel badge + typing indicators'],
      'Alerts & Support': ['Email + Telegram + Discord alerts', '30-day trade history', 'Standard support', 'Referral rewards'],
    },
  },
  {
    id: 'trader', label: 'Trader', price: 49.99, monthlyPrice: 65.99,
    color: 'border-cyan-500/40', accentColor: 'text-cyan-400', highlight: true, badge: 'Most Popular',
    tagline: 'Pro-level execution: futures, algos, conditional bots, copy trading',
    features: [
      'Everything in Starter',
      'Perpetual Futures (8 exchanges, 125x)',
      'Algo Orders (TWAP/VWAP/Iceberg/Bracket)',
      'Conditional / Composite Bots',
      'Two-Way Telegram Trading',
      'Copy Trading (follow up to 10)',
      'AI Signals + Backtests (50/day)',
      'Dual-View Accounting (FIFO/LIFO/HIFO)',
    ],
    detailedFeatures: {
      'Trading': ['Perpetual Futures across 8 CEXs (up to 125x leverage)', 'Algo Orders: TWAP, VWAP, Iceberg, Bracket OCO', 'Conditional / Composite Bots (regime-switching)', 'Two-Way Telegram Trading (/buy, /sell, /close)', 'TradingView Webhooks for auto-execution'],
      'AI & Signals': ['AI Signals (50/day) with confidence scores', 'Real historical backtesting (50/day)', 'NL Bot Builder (50/month)', 'Bayesian probability forecasting'],
      'Engineering Integrity': ['Dual-View Accounting (operational + tax FIFO/LIFO/HIFO)', 'Outbound Webhooks (Discord + Telegram broadcasts)', 'Position Integrity + drift detection', 'Real backtests only (no synthetic scenarios)'],
      'Copy Trading & Marketplace': ['Copy Trading (follow up to 10 verified traders)', 'Browse + buy verified strategies', 'Bot cloning across strategies'],
      'Alerts & Support': ['Email + Telegram + Discord + Webhook alerts', '90-day trade history', 'Priority support', 'Referral rewards'],
    },
  },
  {
    id: 'pro', label: 'Pro', price: 75.99, monthlyPrice: 99.99,
    color: 'border-violet-500/40', accentColor: 'text-violet-400', highlight: false, badge: 'Professional',
    tagline: 'Monetize your skill: create Paid Channels, sell strategies, pro analytics',
    features: [
      'Everything in Trader',
      'Create Paid Channels (earn 80% USDC)',
      'Portfolio Analytics (8-tab dashboard)',
      'Whale Watcher + Smart Money tracking',
      'AI Risk Analyst',
      'MEXC integration (high-leverage)',
      'Verified Track Record — sell on Marketplace',
      'Copy Trading (unlimited)',
    ],
    detailedFeatures: {
      'Monetization': ['Create Paid Channels (1-3 subscription tiers in USDC)', '80% creator / 20% platform split on every subscription', 'Instant on-chain payouts (Base/Polygon/Arbitrum)', 'Verified Track Record — list your strategies'],
      'Pro Analytics': ['Portfolio Analytics (8-tab Bloomberg dashboard)', 'Whale Watcher + Smart Money tracking', 'AI Risk Analyst — conversational on your data', 'Cross-exchange arbitrage detection', 'Portfolio rebalancing engine'],
      'Trading': ['MEXC integration (highest leverage available)', 'AI Strategy Generator (NL → rules)', 'Unlimited NL Bot Builder usage', 'Unlimited AI signals', '500 backtests/day'],
      'Alerts & Support': ['All alert types + SMS', '1-year trade history', 'Dedicated support', 'Referral rewards'],
    },
  },
  {
    id: 'elite', label: 'Elite', price: 149.99, monthlyPrice: 199.99,
    color: 'border-amber-400/60', accentColor: 'text-amber-400', highlight: false, badge: 'Exclusive',
    tagline: '17 institutional tools no other platform has. Built for serious professionals + token issuers.',
    features: [
      'Everything in Pro · Unlimited everything',
      'Multi-Account Aggregator (CEX + DEX)',
      'AI Strategy Coach + Quant Lab',
      'Institutional Reports (Bloomberg-grade PDFs)',
      'Token Launch Scanner + Whale Club access',
      'Tokenomics Studio + AI Advisor',
      'Launch Roadmap + Volume Forecaster',
      'Stripe Connect payouts + Founder access',
    ],
    detailedFeatures: {
      "Trader's Suite": ['Multi-Account Aggregator (unify CEX + DEX + wallets in one view)', 'Tax-Loss Harvest Engine (with replacement map)', 'Institutional Reports (Bloomberg-grade PDFs, audit-ready)', 'AI Strategy Coach (grounded in YOUR data)', 'Quant Lab (factor models, signal correlations)', 'Uniswap V3 LP Studio (range optimizer + IL calculator)', 'Token Launch Scanner (24/7 honeypot detection)'],
      'Issuer Tools': ['DEX Liquidity Optimizer (exact liquidity for target volume)', 'Listing Readiness Scorecard (5 major CEXs)', 'Launch Volume Forecaster (real CoinGecko comparables)', 'Launch Roadmap Planner (4 phased templates)', 'Volume Health Widget (embeddable SVG)', 'Competitor Activity Tracker (5 bot detection indicators)'],
      'Tokenomics': ['AI Tokenomics Advisor (PDF whitepaper analysis)', 'Tokenomics Studio (whitepaper-ready output)'],
      'Exclusive Access': ['Elite Lounge + Whale Club (private chat rooms)', 'Direct founder access + weekly AMAs', 'Elite-only reactions + badges', 'Stripe Connect creator payouts'],
      'Alerts & Support': ['All alert types + SMS + Discord + Custom', 'Unlimited trade history', 'Dedicated priority support', 'Early access to new features'],
    },
  },
];

// ── Testimonials ──────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    name: 'Marcus T.', role: 'Quantitative Trader',
    stars: 5,
    text: '"First platform I\'ve seen that runs the same Smart Trade and Grid bots on both DEX and CEX from one dashboard. The unified balance view alone replaced three spreadsheets."'
  },
  {
    name: 'Sarah K.', role: 'DeFi Portfolio Manager',
    stars: 5,
    text: '"The cross-chain gas monitor alone saved us hundreds of dollars in fees. The arbitrage detector has found opportunities we would have missed entirely."'
  },
  {
    name: 'James L.', role: 'Crypto Fund Analyst',
    stars: 4,
    text: '"The strategy marketplace quality is well above what you see on competing platforms. Audited strategies with real performance history — not just backtests."'
  },
];

// ── Stat bar — every number is grounded in a code-verifiable list ─────────────
const STATS = [
  { label: 'Chains Supported',    value: '8' },     // 7 EVM + Solana — see frontend/src/constants/tiers.js
  { label: 'CEX Integrations',    value: '15' },    // backend/src/adapters/exchangeAdapter.js
  { label: 'Verified Verticals',  value: '7' },     // every vertical has a public /policy endpoint
  { label: 'Elite Suite Tools',   value: '17' },    // see FeaturesPage CATEGORIES.elite
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const landingJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is haythix?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `haythix is a multi-chain crypto trading platform combining DEX and CEX bots, copy trading, cross-chain arbitrage, tokenomics tools, and ${PLATFORM_FEATURE_COUNT} features. Every claim is backed by a public API so users can verify methodology.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Which exchanges and chains does haythix support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '15 CEX integrations (Coinbase, Binance US, Kraken, Bybit, OKX, KuCoin, Bitget, Gate.io, MEXC, HTX, Crypto.com, Bitstamp, Bitfinex, Gemini, BitMart) and 15 chains (Ethereum, BSC, Polygon, Arbitrum, Base, Avalanche, Optimism, Solana, Cronos, Fantom, zkSync Era, Mantle, Linea, Scroll, Blast).',
          },
        },
        {
          '@type': 'Question',
          name: 'How does copy trading work on haythix?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Copy trading is real auto-execution, not advisory. Pick a master trader, set your allocation, and trades fire on your exchange the moment the master fires. 20% drawdown stop-out built in. 10% master profit share / 1% platform fee.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is haythix custodial?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. CEX trading uses your exchange API keys (encrypted at rest, never withdraw permission). DEX trading uses your wallet — you sign every transaction. haythix never holds funds.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much does haythix cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '14-day free trial, then 5 tiers: Starter ($10/mo), Trader ($49.99/mo), Pro ($75.99/mo), Elite ($149.99/mo). Annual plans save ~20%. Pay by card or USDC.',
          },
        },
        {
          '@type': 'Question',
          name: 'What bot types does haythix support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Smart Trade (limit + TP/SL), Grid Bot, DCA Safety Bot, Conditional Bot (regime-switching), TradingView webhook automation, Algo Orders (TWAP, VWAP, Iceberg, Bracket), and Arbitrage. Each works on both DEX and CEX.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white overflow-x-hidden">
      <Seo
        title="Multi-chain crypto trading bots, copy trading & arbitrage"
        description="haythix combines DEX + CEX trading bots, real copy trading, cross-chain arbitrage, AI signals, and tokenomics tools across 15 chains and 15 exchanges. 14-day free trial."
        path="/"
        jsonLd={landingJsonLd}
      />

      {/* ── ANNOUNCEMENT BAR ── */}
      <div
        onClick={() => navigate('/advertise')}
        className="w-full bg-gradient-to-r from-violet-900 via-violet-800 to-indigo-900 border-b border-violet-700/50 py-2.5 px-6 cursor-pointer hover:from-violet-800 hover:via-violet-700 hover:to-indigo-800 transition-all group"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm">
          <Megaphone className="w-4 h-4 text-violet-300 flex-shrink-0" />
          <span className="text-violet-200">
            <span className="font-bold text-white">Advertise on haythix</span>
            {' — '}Reach active crypto traders · from $300/day
          </span>
          <span className="text-violet-300 font-semibold group-hover:text-white transition-colors flex items-center gap-1">
            Get Started <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>

      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0b0f]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-400">
            <a onClick={() => navigate('/features')} className="hover:text-amber-300 transition-colors cursor-pointer flex items-center gap-1.5"><Star className="w-3.5 h-3.5" /> Features</a>
            <a onClick={() => navigate('/comparison')} className="hover:text-white transition-colors cursor-pointer">Compare</a>
            <a onClick={() => navigate('/analyze')} className="hover:text-emerald-300 transition-colors cursor-pointer flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Free Tools
            </a>
            <a onClick={() => navigate('/pricing')} className="hover:text-white transition-colors cursor-pointer">Pricing</a>
            <a
              onClick={() => navigate('/advertise')}
              className="hover:text-violet-300 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <Megaphone className="w-3.5 h-3.5" /> Advertise
            </a>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="hidden md:block text-sm text-gray-400 hover:text-white transition-colors px-4 py-2"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register?plan=starter&path=trial')}
              className="text-sm font-semibold bg-amber-400 text-gray-900 px-5 py-2.5 rounded-xl hover:bg-amber-300 transition-colors"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        {/* background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-amber-500/8 rounded-full blur-3xl" />
          <div className="absolute top-32 left-1/4 w-64 h-64 bg-purple-600/6 rounded-full blur-2xl" />
          <div className="absolute top-20 right-1/4 w-48 h-48 bg-blue-600/6 rounded-full blur-2xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-400 text-xs font-medium mb-8">
            <Sparkles className="w-3.5 h-3.5" />
            Verifiable-methodology trading platform
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-[1.08] tracking-tight">
            AI-Powered Crypto<br />
            <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">
              Trading Automation
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-6 leading-relaxed">
            Run multiple bots, wallets, and strategies across exchanges and chains with
            AI-assisted signals, advanced analytics, and institutional-grade automation.
          </p>

          {/* Verifiable-methodology hook (above the fold). No outcome claims —
              describes what we publish, not what users will achieve. */}
          <a href="#verticals"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-colors mb-10">
            <Eye className="w-4 h-4" />
            <span>7 Verticals · 6 Phases — methodology you can read before you sign up</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button
              onClick={() => navigate('/register?plan=starter&path=trial')}
              className="flex items-center justify-center gap-2 bg-amber-400 text-gray-900 font-bold px-8 py-4 rounded-xl hover:bg-amber-300 transition-all hover:scale-105 text-base"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="flex items-center justify-center gap-2 border border-white/10 text-white px-8 py-4 rounded-xl hover:bg-white/5 transition-colors text-base"
            >
              <Play className="w-4 h-4" /> View Pricing
            </button>
          </div>

          {/* stat bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden max-w-3xl mx-auto">
            {STATS.map(s => (
              <div key={s.label} className="bg-[#0d0e14] px-6 py-5 text-center">
                <p className="text-3xl font-bold text-amber-400 mb-1">{s.value}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Everything you need to <span className="text-amber-400">automate at scale</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            From a single strategy to an institutional multi-chain portfolio — haythix grows with your ambition.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(f => (
            <div
              key={f.title}
              className={`group bg-[#0d0e14] border rounded-2xl p-6 transition-all hover:-translate-y-1 ${
                f.badge === 'ELITE'
                  ? 'border-amber-400/15 hover:border-amber-400/30'
                  : f.badge === 'NEW'
                  ? 'border-violet-400/15 hover:border-violet-400/30'
                  : 'border-white/5 hover:border-amber-400/20'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                {f.badge && (
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full tracking-wider ${
                    f.badge === 'ELITE'
                      ? 'bg-gradient-to-r from-amber-400/20 to-orange-400/20 text-amber-400 border border-amber-400/25'
                      : 'bg-violet-400/15 text-violet-400 border border-violet-400/25'
                  }`}>
                    {f.badge}
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SIX VERTICALS — signature depth moats ── */}
      <section id="verticals" className="bg-gradient-to-b from-[#0a0b0f] to-[#0d0e14] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-5">
              <ShieldCheck className="w-3.5 h-3.5" />
              Seven signature Verticals
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Seven features <span className="text-emerald-400">no other platform offers</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Seven depth moats built on a single principle: <span className="text-white">verifiability as a forcing function.</span>{' '}
              When real data isn't available, we show <code className="bg-white/5 px-1.5 py-0.5 rounded text-emerald-400">pending</code> — never imputed numbers.
              Every claim below is backed by a public API endpoint anyone can call without an account.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VERTICALS.map(v => (
              <div key={v.n}
                className="group bg-[#0d0e14] border border-emerald-400/10 rounded-2xl p-6 transition-all hover:-translate-y-1 hover:border-emerald-400/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${v.color} flex items-center justify-center`}>
                      <v.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[10px] font-bold tracking-widest text-emerald-400/70">VERTICAL {v.n}</span>
                  </div>
                </div>
                <h3 className="font-semibold text-white mb-1">{v.title}</h3>
                <p className="text-xs text-emerald-400/80 mb-3 italic">{v.tagline}</p>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{v.body}</p>
                <div className="pt-3 border-t border-white/5 flex items-center gap-2 text-[11px] text-gray-500">
                  <span className="font-mono text-emerald-400/60">{v.endpoint}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-3xl mx-auto bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 text-sm text-gray-300">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-white mb-1">Verifiable today, no signup required</div>
                <p className="text-gray-400 leading-relaxed">
                  Every Vertical above publishes a public read-only endpoint. If we ever return imputed numbers instead of an empty / pending state, that's a bug — and you can prove it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SIX PHASES — foundation parity ── */}
      <section id="phases" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-5">
            <Layers className="w-3.5 h-3.5" />
            Six engineering Phases
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            The foundation that puts us <span className="text-amber-400">at parity — or ahead</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Before the Verticals, six platform-parity phases. Phase 4 — the QA sweep that strips fabricated metrics — is the one competitors don't ship.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PHASES.map(p => (
            <div key={p.n}
              className="bg-[#0d0e14] border border-white/5 rounded-2xl p-5 hover:border-amber-400/20 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
                  <p.icon className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-[10px] font-bold tracking-widest text-amber-400/70">PHASE {p.n}</span>
              </div>
              <h3 className="font-semibold text-white text-sm mb-1.5">{p.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── COMPARISON ── */}
      <section id="compare" className="bg-[#0d0e14] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              haythix vs <span className="text-amber-400">5 top competitors</span>
            </h2>
            <p className="text-gray-400 max-w-lg mx-auto">
              Every feature that 5 leading platforms offer separately — haythix combines into one. Plus community, AI, and Elite tools you won't find anywhere else.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-white/5">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-5 py-4 text-gray-400 font-medium w-56">Feature</th>
                  <th className="px-3 py-4 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-amber-400 font-bold text-sm">haythix</span>
                      <span className="text-[9px] text-amber-400/60 bg-amber-400/10 px-2 py-0.5 rounded-full">You are here</span>
                    </div>
                  </th>
                  <th className="px-3 py-4 text-center text-gray-400 font-medium text-xs">3Commas</th>
                  <th className="px-3 py-4 text-center text-gray-400 font-medium text-xs">Cryptohopper</th>
                  <th className="px-3 py-4 text-center text-gray-400 font-medium text-xs">Bitsgap</th>
                  <th className="px-3 py-4 text-center text-gray-400 font-medium text-xs">Pionex</th>
                  <th className="px-3 py-4 text-center text-gray-400 font-medium text-xs">Coinrule</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-white/5 ${i % 2 === 0 ? '' : 'bg-white/[0.02]'}`}>
                    <td className="px-5 py-3 text-gray-300 text-xs">{row.feature}</td>
                    <td className="px-3 py-3 bg-amber-400/[0.04]"><CompIcon val={row.haythix} /></td>
                    <td className="px-3 py-3"><CompIcon val={row.commas} /></td>
                    <td className="px-3 py-3"><CompIcon val={row.hopper} /></td>
                    <td className="px-3 py-3"><CompIcon val={row.bitsgap} /></td>
                    <td className="px-3 py-3"><CompIcon val={row.pionex} /></td>
                    <td className="px-3 py-3"><CompIcon val={row.coinrule} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-5 py-3 bg-white/[0.02]">
              <div className="flex items-center gap-5 text-xs text-gray-500">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Supported</span>
                <span className="flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 text-amber-400" /> Limited</span>
                <span className="flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-gray-600" /> Not available</span>
              </div>
              <button
                onClick={() => navigate('/comparison')}
                className="text-xs text-amber-400 hover:text-amber-300 font-medium flex items-center gap-1 transition-colors"
              >
                View full comparison ({COMPARISON.length}+ more features) <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FREE TOOLS ── */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <span className="text-xs font-bold tracking-widest text-emerald-400 uppercase bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">100% Free · No Signup Required</span>
          <h2 className="text-3xl font-bold mt-4 mb-3">
            Free <span className="text-amber-400">Public Tools</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Use our professional-grade analysis tools for free. No account needed — just paste an address and go.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div
            onClick={() => navigate('/analyze')}
            className="bg-[#0d0e14] border border-white/5 rounded-2xl p-8 hover:border-amber-500/30 transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">Token Health Analyzer</h3>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              Instantly detect bot activity, manipulated volume, and rug risks. Get a 6-factor health score for any token on 15 chains.
            </p>
            <span className="text-sm text-amber-400 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              Analyze a Token <ArrowRight className="w-4 h-4" />
            </span>
          </div>
          <div
            onClick={() => navigate('/wallet')}
            className="bg-[#0d0e14] border border-white/5 rounded-2xl p-8 hover:border-violet-500/30 transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-4">
              <Wallet className="w-6 h-6 text-violet-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-violet-400 transition-colors">Wallet Profiler</h3>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              Analyze any wallet's trading performance, win rate, and P&L across multiple chains. Find top traders to follow.
            </p>
            <span className="text-sm text-violet-400 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
              Profile a Wallet <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </section>

      {/* ── ABOUT THE NAME — Ibn al-Haytham brand story ── */}
      <section id="about-name" className="py-20 px-4 bg-gradient-to-b from-[#0a0b0f] to-[#0d0e14] border-y border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-medium mb-5">
            <Eye className="w-3.5 h-3.5" />
            About the name
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
            Named after <span className="text-emerald-300">Ibn al-Haytham</span> — the man who said "verify it yourself"
          </h2>
          <div className="space-y-4 text-gray-400 leading-relaxed">
            <p>
              Hasan Ibn al-Haytham (c. 965–1040) was an Arab polymath whose <em>Book of Optics</em> founded
              the modern empirical method. His thesis was simple and radical for his time:
              <span className="text-white"> conclusions should be tested against observable evidence — not accepted on the word of any teacher, no matter how respected.</span>
            </p>
            <p>
              That principle <em>is</em> haythix. Every Vertical we ship publishes its methodology on a public API endpoint
              anyone can read without an account. Wilson 95% confidence intervals on real data. Transparent "pending" empty
              states. SHA-256 methodology hashes for reproducibility. 365-day audit trails.
            </p>
            <p>
              The platform's job is to give you tools and tell you exactly how those tools work — so you can verify,
              test, and decide for yourself. That's the eight-centuries-old principle the brand is named for.
            </p>
            <p className="text-xs text-gray-500 italic pt-2">
              haythix is a verifiable-methodology platform. Tools, not guarantees. Not financial advice.
            </p>
          </div>
        </div>
      </section>

      {/* ── PRICING CTA ── */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Start trading <span className="text-amber-400">smarter</span> today
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Free trial — no credit card required. Upgrade anytime to unlock premium features.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button onClick={() => navigate('/register?plan=starter&path=trial')} className="px-8 py-3.5 rounded-xl font-semibold text-base bg-accent-primary text-dark-900 hover:bg-accent-primary/90 transition-all">
              Start Free Trial
            </button>
            <button onClick={() => navigate('/pricing')} className="px-8 py-3.5 rounded-xl font-semibold text-base border border-white/10 text-white hover:bg-white/5 transition-all">
              View Plans & Pricing
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-4">First 100 users get 50% off forever — Founders Program</p>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">
            Trusted by <span className="text-amber-400">professional traders</span>
          </h2>
          <p className="text-gray-400">What institutional traders and DeFi professionals say about haythix.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map(t => (
            <div key={t.name} className="bg-[#0d0e14] border border-white/5 rounded-2xl p-6">
              <div className="flex gap-0.5 mb-4">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-5 italic">{t.text}</p>
              <div>
                <p className="font-semibold text-white text-sm">{t.name}</p>
                <p className="text-xs text-gray-500">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ADVERTISE WITH US ── */}
      <section className="bg-[#0d0e14] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-violet-700/20 p-10 md:p-14"
            style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #0d0e14 50%, #0a0e1e 100%)' }}>
            {/* Glow decorations */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-violet-600/8 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-amber-500/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-xs font-medium mb-6">
                  <Megaphone className="w-3.5 h-3.5" />
                  Self-Serve Advertising
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  Reach <span className="text-violet-300">active crypto traders</span><br />
                  directly on the platform
                </h2>
                <p className="text-gray-400 mb-8 leading-relaxed text-sm md:text-base">
                  Place your brand in front of a highly engaged audience of crypto traders,
                  DeFi investors, and blockchain professionals. Full self-service — build your
                  creative, choose your slot, pay once, and go live after a quick review.
                  Ads turn off automatically. No manual intervention required.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    { label: 'Header Banner', size: '728×90 px', price: 'from $500 / day' },
                    { label: 'Sidebar Rectangle', size: '300×250 px', price: 'from $300 / day' },
                  ].map(slot => (
                    <div key={slot.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <p className="text-white font-semibold text-sm mb-1">{slot.label}</p>
                      <p className="text-gray-500 text-xs mb-2 font-mono">{slot.size}</p>
                      <p className="text-amber-400 text-sm font-bold">{slot.price}</p>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/advertise')}
                  className="inline-flex items-center gap-2.5 bg-violet-600 hover:bg-violet-500 text-white font-bold px-7 py-3.5 rounded-xl transition-all hover:scale-105 text-sm"
                >
                  <Megaphone className="w-4 h-4" />
                  Get Started — Place Your Ad
                </button>
              </div>

              {/* Right: metrics */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '50K+', label: 'Daily Active Traders', color: 'text-violet-300' },
                  { value: '7',    label: 'Blockchains Supported', color: 'text-amber-400' },
                  { value: '24h',  label: 'Minimum Placement',    color: 'text-emerald-400' },
                  { value: '100%', label: 'Self-Service',          color: 'text-blue-400' },
                ].map(s => (
                  <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                    <p className={`text-3xl font-bold mb-1 ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-gray-500 leading-snug">{s.label}</p>
                  </div>
                ))}
                <div className="col-span-2 bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-400 leading-relaxed">Ad reviewed by our team — usually within a few hours. Timer starts from approval, not payment. Slot expires automatically — no cancellation needed.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-gradient-to-b from-[#0d0e14] to-[#0a0b0f] py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 mx-auto mb-8">
            <img src={logo} alt="haythix" className="w-full h-full" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">
            Ready to automate<br />your trading strategy?
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            Run advanced automation across 15 DEX chains and 15 CEX exchanges with AI-assisted intelligence — methodology you can read before you sign up.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register?plan=starter&path=trial')}
              className="flex items-center justify-center gap-2 bg-amber-400 text-gray-900 font-bold px-8 py-4 rounded-xl hover:bg-amber-300 transition-all hover:scale-105 text-base"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="border border-white/10 text-white px-8 py-4 rounded-xl hover:bg-white/5 transition-colors text-base font-medium"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <PublicFooter />
    </div>
  );
}
